declare global {
    interface Window {
        dataLayer: Record<string, any>[];
    }
}

async function hashValue(value: string): Promise<string | undefined> {
    if (!value) return undefined;
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(byte => byte.toString(16).padStart(2, "0")).join("");
}

const shouldExcludeData = (emailValue?: string) => emailValue?.includes("@consumerintel.com") ? "Yes" : "No";

export interface PageViewParams {
    eventName?: string;
    pageTitle: string;
    pagePath: string;
    additionalParams?: Record<string, unknown>;
}

export interface PageInfoParam {
    page: {
        title: string;
        url: string;
    }
}

export const GA4Helper = {
    /**
     * Pushes data to the `dataLayer`.
     */
    pushDataLayer(data: Record<string, unknown>): void {
        if (typeof window.dataLayer !== "undefined") {
            window.dataLayer.push(data);
        } else {
            console.warn("Data Layer not found!");
        }
    },

    /**
     * Track a page view event with optional ecommerce data.
     */
    async trackPageView({
        eventName = "spa_page_view",
        pageTitle,
        pagePath,
        additionalParams = {}
    }: PageViewParams): Promise<void> {
        const pageData: Record<string, unknown> = {
            title: pageTitle,
            url: pagePath
        };

        const { useProposalStore } = await import("@/stores/risk/risk");
        const proposalStore = useProposalStore();

        const storedRisk = proposalStore.proposal?.risk;
        const quote = proposalStore.proposal?.quote;

        const emailValue = storedRisk?.proposer?.email ?? undefined;
        const excludeData = shouldExcludeData(emailValue);

        // List of events for which ecommerce object data should be built/included
        const ecommerceEvents = [
            "quotePriceViewA",
            "quotePriceViewB",
            "paymentInfobankdetails",
            "paymentInfocreditcardrecurring",
            "paymentInfocreditcardfull",
            "purchase"
        ];

        const ecommerceData = ecommerceEvents.includes(eventName)
            ? this.buildEcommerceData(quote, proposalStore.proposal?.is_pay_monthly)
            : undefined;

        const filteredParams: Record<string, unknown> = await this.filterParams(additionalParams);
        Object.assign(pageData, filteredParams);

        const data = {
            event: eventName,
            page: pageData,
            ExcludeData: excludeData,
            ...(ecommerceData && { ecommerce: ecommerceData })
        };

        this.pushDataLayer(data);
    },

    /**
     * Builds the ecommerce data object for GA4.
     */
    buildEcommerceData(
        quote: Record<string, any> | undefined,
        isPayMonthly: boolean | undefined
    ): Record<string, unknown> | undefined {
        if (!quote) return undefined;

        const premium = parseFloat(quote.premium || "0");
        const tax = parseFloat(quote.levy_amount || "0");
        const totalExcess = parseFloat(quote.compulsory_excess || "0");

        return {
            currency: "EUR",
            value: premium,
            tax: tax,
            shipping: 0,
            affiliation: "An Post Insurance",
            transaction_id: quote.reference,
            excess: totalExcess,
            items: [
                {
                    item_name: "Home Insurance",
                    item_id: "Home Insurance",
                    price: premium,
                    item_brand: quote.insurer_name || "Unknown Insurer",
                    item_category: "Buildings Contents",
                    item_variant: isPayMonthly ? "Monthly" : "Full pay",
                    quantity: 1
                }
            ]
        };
    },

    /**
     * Filters params and applies hashing for sensitive fields.
     */
    async filterParams(params: Record<string, unknown>): Promise<Record<string, unknown>> {
        const filteredParams: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(params)) {
            const isHash = key === "user_id";
            filteredParams[key] = typeof value === "string" && isHash ? await hashValue(value) : value;
        }
        return filteredParams;
    },

    /**
     * Track a "retrieveQuoteView" event with quote details.
     */
    async trackRetrieveQuote(transactionId: string, recalledFromOffline: boolean): Promise<void> {
        const isOfflineQuote = recalledFromOffline ? "yes" : "no";
        const data = {
            event: "retrieveQuoteView",
            page: {
                title: document.title,
                url: window.location.pathname
            },
            quote: {
                transaction_id: transactionId,
                status: "retrieved",
                call_centre_recall: isOfflineQuote
            }
        };

        this.pushDataLayer(data);
    },

    /**
     * Tracks a virtual pageview event with a given path and title.
     */
    trackVirtualPageView(pagePath: string, pageTitle?: string): void {
        const data = {
            event: "virtualPageview",
            pagePath: pagePath,
            pageTitle: pageTitle || document.title
        };

        this.pushDataLayer(data);
    },

    /**
     * QuoteRefer/ Error
     */
    trackQuoteRefer(errorMessage: string, errorCode?: string, transactionId?: string, pageInfo?: PageInfoParam): void {
        const errorData = {
            message: errorMessage,
            code: errorCode || "N/A",
            transaction_id: transactionId || "N/A",
            pagePath: window.location.pathname
        };

        import("@/stores/risk/risk")
            .then(({ useProposalStore }) => {
                const proposalStore = useProposalStore();

                const quoteTransactionId = proposalStore.proposal?.quote?.reference;
                if (quoteTransactionId) {
                    errorData.transaction_id = quoteTransactionId;
                }

                const storedRisk = proposalStore.proposal?.risk;
                const emailValue = storedRisk?.proposer?.email ?? undefined;
                const excludeData = shouldExcludeData(emailValue);
                let additionalInfo = {};
                if (pageInfo) {
                    additionalInfo = {
                        ...pageInfo,
                        ExcludeData: excludeData
                    }
                }

                this.pushDataLayer({
                    event: "quoteRefer",
                    error: errorData,
                    ...additionalInfo
                });
            })
            .catch(e => {
                this.pushDataLayer({
                    event: "quoteRefer",
                    error: errorData
                });
            });
    }
};
